/**
 * Filtros de la tabla de productos del backoffice (ADM-02): de la URL a un
 * objeto y de vuelta, y la lista de los que estan puestos.
 *
 * La URL es la unica fuente: asi un filtro sobrevive a recargar, a ir al
 * detalle y volver, y se puede pasar el enlace a otra persona. Los nombres
 * son los mismos que la query del backend, con las listas como parametro
 * repetido (`?categoria=a&categoria=b`).
 */
import type {
  FiltrosProductosAdminDto,
  OpcionesFiltroProductos,
  OrdenProductosAdmin,
} from '@bandu/compartido';

import { booleano, cantidad, compactar, lista, texto } from './parametros-url';
import type { FiltroActivo as FiltroActivoDe } from './parametros-url';

export type FiltrosProductos = Omit<FiltrosProductosAdminDto, 'pagina' | 'porPagina'>;

export const ORDENES_PRODUCTOS: { valor: OrdenProductosAdmin; texto: string }[] = [
  { valor: 'nombre', texto: 'Nombre (A a Z)' },
  { valor: 'stock_desc', texto: 'Mas stock primero' },
  { valor: 'stock_asc', texto: 'Menos stock primero' },
  { valor: 'precio_desc', texto: 'Mayor precio' },
  { valor: 'precio_asc', texto: 'Menor precio' },
  { valor: 'recientes', texto: 'Recien llegados' },
];

export function leerFiltros(parametros: URLSearchParams): FiltrosProductos {
  const visibilidad = parametros.get('visibilidad');
  const orden = ORDENES_PRODUCTOS.find((opcion) => opcion.valor === parametros.get('orden'));

  return compactar({
    termino: texto(parametros.get('termino')),
    lineas: lista(parametros, 'linea'),
    categorias: lista(parametros, 'categoria'),
    marcas: lista(parametros, 'marca'),
    visibilidad: visibilidad === 'publicado' || visibilidad === 'oculto' ? visibilidad : undefined,
    conFoto: booleano(parametros.get('conFoto')),
    conPrecio: booleano(parametros.get('conPrecio')),
    stockMin: cantidad(parametros.get('stockMin')),
    stockMax: cantidad(parametros.get('stockMax')),
    soloDestacados: parametros.get('soloDestacados') === 'true' ? true : undefined,
    // El orden por defecto no se escribe: la URL sin filtros queda limpia.
    orden: orden !== undefined && orden.valor !== 'nombre' ? orden.valor : undefined,
  });
}

export function escribirFiltros(filtros: FiltrosProductos): URLSearchParams {
  const parametros = new URLSearchParams();
  const poner = (clave: string, valor: string | number | boolean | undefined) => {
    if (valor !== undefined) parametros.set(clave, String(valor));
  };
  const ponerLista = (clave: string, valores: string[] | undefined) => {
    for (const valor of valores ?? []) parametros.append(clave, valor);
  };

  poner('termino', filtros.termino);
  ponerLista('linea', filtros.lineas);
  ponerLista('categoria', filtros.categorias);
  ponerLista('marca', filtros.marcas);
  poner('visibilidad', filtros.visibilidad);
  poner('conFoto', filtros.conFoto);
  poner('conPrecio', filtros.conPrecio);
  poner('stockMin', filtros.stockMin);
  poner('stockMax', filtros.stockMax);
  if (filtros.soloDestacados === true) poner('soloDestacados', true);
  if (filtros.orden !== undefined && filtros.orden !== 'nombre') poner('orden', filtros.orden);
  return parametros;
}

/** Aplica un cambio sobre los filtros actuales y devuelve el objeto limpio. */
export function conCambio(
  filtros: FiltrosProductos,
  cambio: Partial<FiltrosProductos>,
): FiltrosProductos {
  const siguiente = { ...filtros, ...cambio };
  // Una lista vacia es lo mismo que no filtrar por ella.
  for (const clave of ['lineas', 'categorias', 'marcas'] as const) {
    if (siguiente[clave]?.length === 0) siguiente[clave] = undefined;
  }
  return compactar(siguiente);
}

/** Limpiar todo quita los filtros pero respeta el orden: ordenar no es filtrar. */
export function sinFiltros(filtros: FiltrosProductos): FiltrosProductos {
  return compactar({ orden: filtros.orden });
}

export type ModoStock = 'todos' | 'con' | 'sin' | 'rango';

/**
 * "Con stock" y "Sin stock" son rangos con nombre (1 o mas, exactamente 0):
 * el backend solo conoce minimo y maximo.
 */
export function modoStock(filtros: FiltrosProductos): ModoStock {
  const { stockMin: minimo, stockMax: maximo } = filtros;
  if (minimo === undefined && maximo === undefined) return 'todos';
  if (minimo === 1 && maximo === undefined) return 'con';
  if ((minimo === undefined || minimo === 0) && maximo === 0) return 'sin';
  return 'rango';
}

export type FiltroActivo = FiltroActivoDe<FiltrosProductos>;

function textoStock(filtros: FiltrosProductos): string {
  const { stockMin: minimo, stockMax: maximo } = filtros;
  switch (modoStock(filtros)) {
    case 'con':
      return 'Con stock';
    case 'sin':
      return 'Sin stock';
    default:
      if (minimo !== undefined && maximo !== undefined) return `Stock entre ${minimo} y ${maximo}`;
      if (minimo !== undefined) return `Stock desde ${minimo}`;
      return `Stock hasta ${maximo ?? 0}`;
  }
}

/**
 * Un chip por filtro puesto, en el orden de la barra. Linea, categoria y
 * marca van uno por valor: quitar "Brocas" no tiene por que quitar tambien
 * "Discos".
 */
export function filtrosActivos(
  filtros: FiltrosProductos,
  opciones: OpcionesFiltroProductos | null,
): FiltroActivo[] {
  const activos: FiltroActivo[] = [];
  const agregar = (clave: string, texto: string, cambio: Partial<FiltrosProductos>) => {
    activos.push({ clave, texto, sinEste: conCambio(filtros, cambio) });
  };

  const nombreLinea = new Map(opciones?.lineas.map((linea) => [linea.slug, linea.nombre]));
  const nombreCategoria = new Map(
    opciones?.lineas.flatMap((linea) =>
      linea.categorias.map((categoria) => [categoria.slug, categoria.nombre] as const),
    ),
  );

  if (filtros.termino !== undefined) {
    agregar('termino', `Busqueda: "${filtros.termino}"`, { termino: undefined });
  }
  for (const slug of filtros.lineas ?? []) {
    agregar(`linea-${slug}`, `Linea: ${nombreLinea.get(slug) ?? slug}`, {
      lineas: filtros.lineas?.filter((otro) => otro !== slug),
    });
  }
  for (const slug of filtros.categorias ?? []) {
    agregar(`categoria-${slug}`, `Categoria: ${nombreCategoria.get(slug) ?? slug}`, {
      categorias: filtros.categorias?.filter((otro) => otro !== slug),
    });
  }
  for (const marca of filtros.marcas ?? []) {
    agregar(`marca-${marca}`, `Marca: ${marca}`, {
      marcas: filtros.marcas?.filter((otra) => otra !== marca),
    });
  }
  if (filtros.visibilidad !== undefined) {
    agregar('visibilidad', filtros.visibilidad === 'publicado' ? 'Publicados' : 'Ocultos', {
      visibilidad: undefined,
    });
  }
  if (filtros.conFoto !== undefined) {
    agregar('conFoto', filtros.conFoto ? 'Con foto' : 'Sin foto', { conFoto: undefined });
  }
  if (filtros.conPrecio !== undefined) {
    agregar('conPrecio', filtros.conPrecio ? 'Con precio' : 'Sin precio', { conPrecio: undefined });
  }
  if (modoStock(filtros) !== 'todos') {
    agregar('stock', textoStock(filtros), { stockMin: undefined, stockMax: undefined });
  }
  if (filtros.soloDestacados === true) {
    agregar('soloDestacados', 'Destacados en portada', { soloDestacados: undefined });
  }

  return activos;
}
